import React, { useState } from 'react';
import { CreditCard, Check, Loader2, AlertTriangle, CheckCircle, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Demo Card Form - Simula pagamento senza Stripe reale
const DemoCardForm = ({ amount, description, onSuccess, onError, billingData }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [cardError, setCardError] = useState('');

  // Format card number
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  // Format expiry
  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  // Validate demo card
  const validateCard = () => {
    const cleanCard = cardNumber.replace(/\s/g, '');
    
    // Carte di test accettate
    const testCards = ['4242424242424242', '4000056655665556', '5555555555554444', '378282246310005'];
    
    if (cleanCard.length < 13) {
      setCardError('Numero carta non valido');
      return false;
    }
    
    if (!expiry || expiry.length < 5) {
      setCardError('Scadenza non valida (MM/YY)');
      return false;
    }
    
    if (!cvc || cvc.length < 3) {
      setCardError('CVC non valido');
      return false;
    }
    
    setCardError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateCard()) return;
    
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      // 1. Create demo payment intent
      const intentResponse = await axios.post(
        `${API}/api/payments/create-payment-intent`,
        {
          amount: amount,
          description: description,
          billing_data: billingData,
          demo_mode: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { payment_intent_id, demo_mode } = intentResponse.data;
      
      if (demo_mode) {
        // 2. Simulate card processing delay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // 3. Confirm demo payment
        const confirmResponse = await axios.post(
          `${API}/api/payments/demo/confirm`,
          {
            payment_intent_id: payment_intent_id,
            card_last4: cardNumber.replace(/\s/g, '').slice(-4)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        onSuccess?.(confirmResponse.data);
        toast.success('🎉 Pagamento demo completato con successo!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      onError?.(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Demo Mode Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-amber-800">Modalità Demo Attiva</p>
          <p className="text-sm text-amber-700">
            Nessun addebito reale. Usa la carta test: <code className="bg-amber-100 px-1 rounded">4242 4242 4242 4242</code>
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Card Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Numero Carta
          </label>
          <div className="relative">
            <input
              type="text"
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
              placeholder="4242 4242 4242 4242"
              maxLength={19}
              className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
              data-testid="card-number-input"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1">
              <img src="https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg" alt="Visa" className="h-6" />
              <img src="https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg" alt="MC" className="h-6" />
            </div>
          </div>
        </div>

        {/* Expiry & CVC */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scadenza
            </label>
            <input
              type="text"
              value={expiry}
              onChange={(e) => setExpiry(formatExpiry(e.target.value))}
              placeholder="MM/YY"
              maxLength={5}
              className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
              data-testid="card-expiry-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CVC
            </label>
            <input
              type="text"
              value={cvc}
              onChange={(e) => setCvc(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              placeholder="123"
              maxLength={4}
              className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
              data-testid="card-cvc-input"
            />
          </div>
        </div>

        {/* Error */}
        {cardError && (
          <p className="text-red-600 text-sm">{cardError}</p>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isProcessing}
          className="w-full py-4 px-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50"
          style={{ background: '#2A9FCB' }}
          data-testid="pay-button"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Elaborazione in corso...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Paga €{amount?.toFixed(2)} (Demo)
            </>
          )}
        </button>
      </form>
    </div>
  );
};

// PayPal Demo
const PayPalDemo = ({ amount, description, onSuccess, onError, billingData }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePayPalDemo = async () => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create demo PayPal order
      const response = await axios.post(
        `${API}/api/payments/create-payment-intent`,
        {
          amount: amount,
          description: description + ' (PayPal Demo)',
          billing_data: billingData,
          demo_mode: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await new Promise(resolve => setTimeout(resolve, 2000));

      // Confirm
      const confirmResponse = await axios.post(
        `${API}/api/payments/demo/confirm`,
        {
          payment_intent_id: response.data.payment_intent_id,
          card_last4: 'PYPL'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onSuccess?.(confirmResponse.data);
      toast.success('🎉 Pagamento PayPal demo completato!');
    } catch (err) {
      onError?.(err.response?.data?.detail || err.message);
      toast.error('Errore PayPal demo');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-blue-800">PayPal Demo</p>
          <p className="text-sm text-blue-700">Simulazione pagamento PayPal senza addebito reale.</p>
        </div>
      </div>

      <button
        onClick={handlePayPalDemo}
        disabled={isProcessing}
        className="w-full py-4 px-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        style={{ background: '#0070BA' }}
        data-testid="paypal-demo-button"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Connessione a PayPal...
          </>
        ) : (
          <>
            <svg className="h-5" viewBox="0 0 101 32" fill="currentColor">
              <path d="M12.237 4.047H4.556c-.43 0-.795.313-.862.737L.787 24.59c-.05.323.2.615.527.615h3.665c.43 0 .795-.313.862-.736l.808-5.128c.066-.424.431-.737.862-.737h1.988c4.14 0 6.528-2.003 7.152-5.975.281-1.736.011-3.1-.8-4.056-.893-1.05-2.475-1.526-4.614-1.526z" fill="white"/>
            </svg>
            Paga con PayPal (Demo) - €{amount?.toFixed(2)}
          </>
        )}
      </button>
    </div>
  );
};

// Apple Pay / Google Pay Demo
const WalletPayDemo = ({ amount, description, onSuccess, onError, billingData, walletType }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleWalletDemo = async () => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/api/payments/create-payment-intent`,
        {
          amount: amount,
          description: description + ` (${walletType} Demo)`,
          billing_data: billingData,
          demo_mode: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Simulate wallet authentication
      await new Promise(resolve => setTimeout(resolve, 1800));

      const confirmResponse = await axios.post(
        `${API}/api/payments/demo/confirm`,
        {
          payment_intent_id: response.data.payment_intent_id,
          card_last4: walletType === 'Apple Pay' ? 'APPL' : 'GPAY'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onSuccess?.(confirmResponse.data);
      toast.success(`🎉 Pagamento ${walletType} demo completato!`);
    } catch (err) {
      onError?.(err.response?.data?.detail || err.message);
      toast.error(`Errore ${walletType} demo`);
    } finally {
      setIsProcessing(false);
    }
  };

  const isApple = walletType === 'Apple Pay';

  return (
    <div className="space-y-4">
      <div className={`${isApple ? 'bg-gray-50 border-gray-200' : 'bg-green-50 border-green-200'} border rounded-xl p-4 flex items-start gap-3`}>
        <Smartphone className={`w-5 h-5 ${isApple ? 'text-gray-600' : 'text-green-600'} flex-shrink-0 mt-0.5`} />
        <div>
          <p className={`font-semibold ${isApple ? 'text-gray-800' : 'text-green-800'}`}>{walletType} Demo</p>
          <p className={`text-sm ${isApple ? 'text-gray-700' : 'text-green-700'}`}>
            Simulazione {walletType} senza addebito reale.
          </p>
        </div>
      </div>

      <button
        onClick={handleWalletDemo}
        disabled={isProcessing}
        className="w-full py-4 px-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        style={{ background: isApple ? '#000' : '#4285F4' }}
        data-testid={`${walletType.toLowerCase().replace(' ', '-')}-demo-button`}
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Autenticazione in corso...
          </>
        ) : (
          <>
            {isApple ? (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.41-1.09-.48-2.08-.5-3.23 0-1.44.62-2.2.44-3.06-.41C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972-3.332 0-6.033-2.701-6.033-6.032s2.701-6.032 6.033-6.032c1.498 0 2.866.549 3.921 1.453l2.814-2.814C17.503 2.988 15.139 2 12.545 2 7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/>
              </svg>
            )}
            Paga con {walletType} (Demo) - €{amount?.toFixed(2)}
          </>
        )}
      </button>
    </div>
  );
};

// Main Payment Component
export const PaymentForm = ({ 
  amount, 
  description, 
  billingData,
  onSuccess, 
  onError,
  showPayPal = true,
  showApplePay = true,
  showGooglePay = true
}) => {
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  const handleSuccess = (data) => {
    setPaymentComplete(true);
    setPaymentData(data);
    onSuccess?.(data);
  };

  if (paymentComplete) {
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-4">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-green-800 mb-2">Pagamento Completato!</h3>
        <p className="text-gray-600 mb-4">
          {paymentData?.demo_mode && (
            <span className="inline-block bg-amber-100 text-amber-800 px-2 py-1 rounded text-sm mb-2">
              🧪 Modalità Demo
            </span>
          )}
        </p>
        <p className="text-gray-500 text-sm">
          Riceverai una email di conferma a breve.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Payment Method Selector */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <button
          type="button"
          onClick={() => setPaymentMethod('card')}
          className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
            paymentMethod === 'card' 
              ? 'border-[#2A9FCB] bg-[#E8F4FD]' 
              : 'border-gray-200 hover:border-gray-300'
          }`}
          data-testid="select-card"
        >
          <CreditCard className={`w-5 h-5 ${paymentMethod === 'card' ? 'text-[#2A9FCB]' : 'text-gray-500'}`} />
          <span className={`text-xs ${paymentMethod === 'card' ? 'text-[#2A9FCB] font-medium' : 'text-gray-600'}`}>
            Carta
          </span>
        </button>
        
        {showPayPal && (
          <button
            type="button"
            onClick={() => setPaymentMethod('paypal')}
            className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
              paymentMethod === 'paypal' 
                ? 'border-[#0070BA] bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid="select-paypal"
          >
            <svg className={`h-5 w-5 ${paymentMethod === 'paypal' ? 'text-[#0070BA]' : 'text-gray-500'}`} viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.144 19.532l.38-2.442H5.633l1.985-12.537h4.632c1.89 0 3.268.369 4.139 1.106.871.736 1.227 1.827 1.065 3.272-.08.636-.238 1.28-.475 1.934-.236.654-.56 1.25-.97 1.79-.462.604-1.01 1.085-1.643 1.442-.632.358-1.345.603-2.138.735-.381.063-.776.094-1.188.094H8.938l-.464 2.857h3.14l-.366 2.336c-.047.288-.126.516-.238.685-.111.17-.276.295-.493.378-.216.082-.457.123-.721.123H7.144z"/>
            </svg>
            <span className={`text-xs ${paymentMethod === 'paypal' ? 'text-[#0070BA] font-medium' : 'text-gray-600'}`}>
              PayPal
            </span>
          </button>
        )}

        {showApplePay && (
          <button
            type="button"
            onClick={() => setPaymentMethod('apple')}
            className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
              paymentMethod === 'apple' 
                ? 'border-black bg-gray-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid="select-apple-pay"
          >
            <svg className={`h-5 w-5 ${paymentMethod === 'apple' ? 'text-black' : 'text-gray-500'}`} viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.41-1.09-.48-2.08-.5-3.23 0-1.44.62-2.2.44-3.06-.41C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            <span className={`text-xs ${paymentMethod === 'apple' ? 'text-black font-medium' : 'text-gray-600'}`}>
              Apple Pay
            </span>
          </button>
        )}

        {showGooglePay && (
          <button
            type="button"
            onClick={() => setPaymentMethod('google')}
            className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
              paymentMethod === 'google' 
                ? 'border-[#4285F4] bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid="select-google-pay"
          >
            <svg className={`h-5 w-5 ${paymentMethod === 'google' ? 'text-[#4285F4]' : 'text-gray-500'}`} viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972-3.332 0-6.033-2.701-6.033-6.032s2.701-6.032 6.033-6.032c1.498 0 2.866.549 3.921 1.453l2.814-2.814C17.503 2.988 15.139 2 12.545 2 7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z"/>
            </svg>
            <span className={`text-xs ${paymentMethod === 'google' ? 'text-[#4285F4] font-medium' : 'text-gray-600'}`}>
              Google Pay
            </span>
          </button>
        )}
      </div>

      {/* Payment Forms */}
      {paymentMethod === 'card' && (
        <DemoCardForm
          amount={amount}
          description={description}
          billingData={billingData}
          onSuccess={handleSuccess}
          onError={onError}
        />
      )}

      {paymentMethod === 'paypal' && (
        <PayPalDemo
          amount={amount}
          description={description}
          billingData={billingData}
          onSuccess={handleSuccess}
          onError={onError}
        />
      )}

      {paymentMethod === 'apple' && (
        <WalletPayDemo
          amount={amount}
          description={description}
          billingData={billingData}
          onSuccess={handleSuccess}
          onError={onError}
          walletType="Apple Pay"
        />
      )}

      {paymentMethod === 'google' && (
        <WalletPayDemo
          amount={amount}
          description={description}
          billingData={billingData}
          onSuccess={handleSuccess}
          onError={onError}
          walletType="Google Pay"
        />
      )}

      {/* Security Badge */}
      <div className="flex items-center justify-center gap-2 text-xs text-gray-500 pt-4 border-t">
        <Check className="w-4 h-4 text-green-500" />
        <span>Pagamento sicuro con crittografia SSL 256-bit</span>
      </div>
    </div>
  );
};

export default PaymentForm;
